import type { DisciplineFilePackDetails, DisciplineFilePackPayload } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface DisciplineFilePackApiBase {
  disciplineId: string | number
  onStart?: () => void
  onFinish?: () => void
}

interface DisciplineFilePackApiOptions extends DisciplineFilePackApiBase {
  packId: number
}

interface DisciplineFilePackWriteApiOptions extends DisciplineFilePackApiOptions {
  payload: DisciplineFilePackPayload
}

const INVALID_URL = 'Unexpected error. Invalid request URL.'

/** `POST …/files/packs` — opens (or reuses the oldest empty) TUTOR-owned draft the uploads attach to. */
export const useDisciplineFilePackStoreApi = async (
  options: DisciplineFilePackApiBase & { tutorId?: number | null },
): Promise<DisciplineFilePackDetails | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const url = useAdminRoutes('admin.disciplines.api.file-packs', { discipline_id: options.disciplineId })

  options.onStart?.()

  try {
    if (!url) {
      toast.error(INVALID_URL)
      return null
    }

    const body = options.tutorId ? { tutor_id: options.tutorId } : {}
    const { data } = await $axios.post<ApiResponse>(url, body)

    return data.data ?? null
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    options.onFinish?.()
  }
}

/** `GET …/files/packs/{pack}` — the pack with its files (signed links valid for an hour). */
export const useDisciplineFilePackShowApi = async (
  options: DisciplineFilePackApiOptions,
): Promise<DisciplineFilePackDetails | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const url = useAdminRoutes('admin.disciplines.api.file-pack', {
    discipline_id: options.disciplineId,
    pack_id: options.packId,
  })

  options.onStart?.()

  try {
    if (!url) {
      toast.error(INVALID_URL)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    return data.data ?? null
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err) || 'Failed to download files for viewing.')
    return null
  } finally {
    options.onFinish?.()
  }
}

/** `PATCH …/files/packs/{pack}` — answers with the whole pack. */
export const useDisciplineFilePackUpdateApi = async (
  options: DisciplineFilePackWriteApiOptions,
): Promise<DisciplineFilePackDetails | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const url = useAdminRoutes('admin.disciplines.api.file-pack', {
    discipline_id: options.disciplineId,
    pack_id: options.packId,
  })

  options.onStart?.()

  try {
    if (!url) {
      toast.error(INVALID_URL)
      return null
    }

    const { data } = await $axios.patch<ApiResponse>(url, options.payload)
    toast.success('Files edited successfully')

    return data.data ?? null
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    options.onFinish?.()
  }
}

/** `POST …/files/packs/{pack}/send` — sends the draft to the customer once; NOT idempotent. */
export const useDisciplineFilePackSendApi = async (
  options: DisciplineFilePackWriteApiOptions,
): Promise<DisciplineFilePackDetails | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const url = useAdminRoutes('admin.disciplines.api.file-pack.send', {
    discipline_id: options.disciplineId,
    pack_id: options.packId,
  })

  options.onStart?.()

  try {
    if (!url) {
      toast.error(INVALID_URL)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, options.payload)
    toast.success('Your files was successfully sent.')

    return data.data ?? null
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    options.onFinish?.()
  }
}

/** `DELETE …/files/packs/{pack}` — a pack linked to a tutor task is refused with a 422. */
export const useDisciplineFilePackDeleteApi = async (
  options: DisciplineFilePackApiOptions & { silent?: boolean },
): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const url = useAdminRoutes('admin.disciplines.api.file-pack', {
    discipline_id: options.disciplineId,
    pack_id: options.packId,
  })

  options.onStart?.()

  try {
    if (!url) {
      if (!options.silent) toast.error(INVALID_URL)
      return false
    }

    await $axios.delete<ApiResponse>(url)
    if (!options.silent) toast.success('Files was successfully deleted.')

    return true
  } catch (err: unknown) {
    if (!options.silent) toast.error(getAxiosErrorMessage(err))
    return false
  } finally {
    options.onFinish?.()
  }
}
